import type { ErrorDetails, JsonValue } from "./log-entry.model";

/**
 * "Make an arbitrary value JSON-safe" logic, applied once by `Logger` to every entry's `context`
 * before it's persisted — so no capture path (`console-interceptor.ts`, `cc7-logger-bridge.ts`,
 * `app-error-handler.ts`) or application call site needs its own sanitization.
 *
 * Every captured entry is eventually written to `gciins-logs-db` via `IDBObjectStore.put()` and
 * later `JSON.stringify`-ed by the log viewer/export, so anything persisted must be plain JSON.
 */

/**
 * `DOMException` is checked on its own: in most modern browsers it is *also* `instanceof Error`,
 * but that inheritance isn't guaranteed in every browser.
 */
export function isErrorLike(value: unknown): value is Error | DOMException {
  return value instanceof Error || value instanceof DOMException;
}

/**
 * Describes any thrown/rejected value as `ErrorDetails`. `Error`/`DOMException` carry their
 * `name`/`message`/`stack`/`code` as prototype getters that a plain copy would drop, so they're read
 * explicitly. Anything else (e.g. `throw "boom"`, a rejected `HttpErrorResponse`) is described by its
 * `typeof` and `String(value)`, e.g. `{ name: "string", message: "boom" }`.
 *
 * `DOMException` is checked first: since it's usually also `instanceof Error`, checking `Error` first
 * would drop its `code`.
 */
export function toErrorDetails(value: unknown): ErrorDetails {
  if (value instanceof DOMException) {
    return { name: value.name, message: value.message, stack: value.stack, code: value.code };
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  try {
    return { name: typeof value, message: String(value) };
  } catch {
    return { name: typeof value, message: "[Unserializable value]" };
  }
}

/**
 * Copies an arbitrary value into plain JSON with a plain `JSON.stringify` + `JSON.parse`, so it
 * follows `JSON.stringify`'s usual rules (e.g. a `Date` becomes its ISO string, `undefined` and
 * functions are dropped). The only addition is the replacer: an `Error`/`DOMException` at any depth
 * becomes its `ErrorDetails` instead of `{}`.
 *
 * Falls back to `String(value)` when `JSON.stringify` returns nothing (`undefined`, a function) or
 * throws (a circular reference, a `bigint`, a throwing getter).
 */
export function toJsonValue(value: unknown): JsonValue {
  try {
    const json = JSON.stringify(value, expandErrors);
    return json === undefined ? String(value) : JSON.parse(json);
  } catch {
    return String(value);
  }
}

/** `JSON.stringify` replacer — called for every value at every depth, not just the top-level one. */
function expandErrors(_key: string, value: unknown): unknown {
  return isErrorLike(value) ? toErrorDetails(value) : value;
}
